import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Check, X, Search, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useReviews } from "@/contexts/ReviewContext";
import { useProducts } from "@/contexts/ProductContext";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

export default function ManageReviews() {
  const { reviews, replyToReview, removeReview, removeAdminReply } = useReviews();
  const { products } = useProducts();
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const { toast } = useToast();

  const getProductName = (productId: string) => {
    const found = products.find(p => p.id === productId);
    return found ? found.title : productId;
  };

  const handleReply = (reviewId: string) => {
    if (replyText.trim()) {
      replyToReview(reviewId, replyText.trim());
      setReplyingId(null);
      setReplyText("");
    }
  };

  const handleDelete = async (reviewId: string) => {
    try {
      await removeReview(reviewId);
      toast({
        title: "Review Deleted",
        description: "The review was deleted successfully.",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Error Deleting Review",
        description: error?.message || "Failed to delete review. Check your permissions.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveAdminReply = async (reviewId: string) => {
    try {
      await removeAdminReply(reviewId);
      toast({
        title: "Admin Reply Deleted",
        description: "The admin reply was removed from the review.",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Error Deleting Admin Reply",
        description: error?.message || "Failed to delete admin reply.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Manage Reviews</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">
            Moderate and respond to customer reviews
          </p>
        </div>
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search reviews..."
            className="w-full pl-8 sm:w-64 lg:w-80 text-sm sm:text-base"
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Customer Reviews</CardTitle>
          <CardDescription className="text-sm">
            View and manage customer reviews
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {/* Mobile Card View */}
          <div className="block lg:hidden space-y-4">
            {reviews.map((review) => (
              <Card key={review.id} className="p-4 transition-all duration-200 hover:shadow-md">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate">{getProductName(review.productId)}</h4>
                      <p className="text-xs text-gray-500 mt-1">by {review.userName}</p>
                    </div>
                    <div className="flex items-center ml-2">
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 mr-1" />
                      <span className="text-sm">{review.rating}</span>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-700">
                    {review.comment}
                    {review.adminReply && (
                      <div className="mt-2 text-xs text-green-700 bg-green-50 rounded p-2">
                        <div className="flex items-center justify-between">
                          <span><strong>Admin Reply:</strong> {review.adminReply}</span>
                          <Button size="sm" variant="ghost" className="text-red-600 px-2 py-1 h-auto text-xs" onClick={() => handleRemoveAdminReply(review.id)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                    {replyingId === review.id && (
                      <div className="mt-2 space-y-2">
                        <Input
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Type your reply..."
                          className="text-xs"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleReply(review.id)} className="text-xs">
                            Send
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setReplyingId(null)} className="text-xs">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <Badge variant="default" className="text-xs">Visible</Badge>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setReplyingId(review.id)} className="btn-touch">
                        <MessageCircle className="h-3 w-3 mr-1" />
                        Reply
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 btn-touch" onClick={() => handleDelete(review.id)}>
                        <X className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Review</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.map((review) => (
                <TableRow key={review.id}>
                  <TableCell className="font-medium">
                    {getProductName(review.productId)}
                  </TableCell>
                  <TableCell className="font-medium">
                    {review.userName}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 mr-1" />
                      {review.rating}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate">
                    {review.comment}
                    {review.adminReply && (
                      <div className="mt-2 text-xs text-green-700 bg-green-50 rounded p-2 flex items-center gap-2">
                        Admin Reply: {review.adminReply}
                        <Button size="sm" variant="ghost" className="text-red-600 ml-2 px-2 py-0.5" onClick={() => handleRemoveAdminReply(review.id)} title="Delete Admin Reply">
                          Delete Reply
                        </Button>
                      </div>
                    )}
                    {replyingId === review.id && (
                      <div className="mt-2 flex gap-2">
                        <Input
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Type your reply..."
                          className="text-xs"
                          autoFocus
                        />
                        <Button size="sm" onClick={() => handleReply(review.id)}>
                          Send
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setReplyingId(null)}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">Visible</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setReplyingId(review.id)} title="Reply">
                        <MessageCircle className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDelete(review.id)} title="Delete">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
